import React, {useState} from "react";
import * as MuiIcons from "@mui/icons-material";
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link, MenuItem,
    Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from "@mui/material";
import {Delete, InsertDriveFile} from "@mui/icons-material";
import S3FileUpload from "../referentiel/shared/S3FileUpload.js";
import {getPicto} from "../../../utils/pictoCatalog.js";
import {toastHelper} from "../../../utils/toast-helper.js";
import Constants from "../../../utils/constants.js";
import {useGetConsignesQuery} from "../../../modules/consignes/consignes.api.js";
import {
    useGetByConteneurQuery,
    useCreateConteneurConsigneMutation,
    useDeleteConteneurConsigneMutation,
} from "../../../modules/conteneurConsignes/conteneurConsignes.api.js";

function Picto({pictoKey, color}) {
    const entry = getPicto(pictoKey);
    const Cmp = MuiIcons[entry.mui] || MuiIcons.Help;
    return <Cmp sx={{color: color || "inherit"}} fontSize="small"/>;
}

export default function ConteneurConsignesDialog({open, onClose, conteneur, onChanged}) {
    const conteneurIri = conteneur?.["@id"];

    const {data: catalogue = []} = useGetConsignesQuery(undefined, {skip: !open});
    const {data: affectations = []} = useGetByConteneurQuery(conteneurIri, {skip: !open || !conteneurIri});
    const [createConteneurConsigne] = useCreateConteneurConsigneMutation();
    const [deleteConteneurConsigne] = useDeleteConteneurConsigneMutation();

    const [consigne, setConsigne] = useState("");
    const [dateDebut, setDateDebut] = useState("");
    const [dateFin, setDateFin] = useState("");
    const [detail, setDetail] = useState("");
    const [planUrl, setPlanUrl] = useState(null);

    const resetAdd = () => {
        setConsigne("");
        setDateDebut("");
        setDateFin("");
        setDetail("");
        setPlanUrl(null);
    };

    const handleAdd = async () => {
        if (!consigne) return;
        try {
            await createConteneurConsigne({
                conteneurIri,
                conteneur: conteneurIri,
                consigne,
                dateDebut: dateDebut || null,
                dateFin: dateFin || null,
                detail: detail || null,
                fichierPath: planUrl || null,
                fichierVersion: planUrl ? 1 : null,
            }).unwrap();
            toastHelper.displaySuccess("Consigne affectée.");
            resetAdd();
            onChanged?.();
        } catch (e) {
            toastHelper.handleError(e, "Oops, une erreur est survenue.");
        }
    };

    const handleDelete = async (cc) => {
        try {
            await deleteConteneurConsigne({id: cc.id, conteneurIri}).unwrap();
            toastHelper.displaySuccess("Consigne retirée.");
            onChanged?.();
        } catch (e) {
            toastHelper.handleError(e, "Oops, une erreur est survenue.");
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Consignes — {conteneur?.nomenclature}</DialogTitle>
            <DialogContent dividers>
                {/* AFFECTATIONS EXISTANTES */}
                <Typography variant="subtitle2" gutterBottom>Consignes affectées</Typography>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Consigne</TableCell>
                                <TableCell>Validité</TableCell>
                                <TableCell>Détail</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {affectations.length === 0 &&
                                <TableRow><TableCell colSpan={5} align="center">Aucune consigne</TableCell></TableRow>}
                            {affectations.map((cc) => (
                                <TableRow key={cc.id}>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Picto pictoKey={cc.consigne?.picto} color={cc.consigne?.couleur}/>
                                            <span>{cc.consigne?.libelleFr}</span>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        {cc.dateDebut || cc.dateFin
                                            ? `${cc.dateDebut ? new Date(cc.dateDebut).toLocaleDateString() : "…"} → ${cc.dateFin ? new Date(cc.dateFin).toLocaleDateString() : "…"}`
                                            : "Permanente"}
                                    </TableCell>
                                    <TableCell>{cc.detail || "—"}</TableCell>
                                    <TableCell>
                                        {cc.fichierPath
                                            ? <Link href={`${Constants.API_ASSETS_DOCUMENT}/${cc.fichierPath}`} target="_blank" rel="noopener"><InsertDriveFile fontSize="small"/></Link>
                                            : "—"}
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton color="error" size="small" onClick={() => handleDelete(cc)}><Delete/></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* AJOUT */}
                <Typography variant="subtitle2" sx={{mt: 3}} gutterBottom>Affecter une consigne</Typography>
                <Stack spacing={2}>
                    <TextField
                        select label="Consigne" value={consigne} onChange={(e) => setConsigne(e.target.value)} size="small" fullWidth
                    >
                        {[...catalogue].sort((a, b) => (a.libelleFr || "").localeCompare(b.libelleFr || "")).map((c) => (
                            <MenuItem key={c["@id"]} value={c["@id"]}>{c.libelleFr}</MenuItem>
                        ))}
                    </TextField>
                    <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                        <TextField type="date" label="Début de validité" InputLabelProps={{shrink: true}} value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} size="small"/>
                        <TextField type="date" label="Fin de validité" InputLabelProps={{shrink: true}} value={dateFin} onChange={(e) => setDateFin(e.target.value)} size="small"/>
                    </Stack>
                    <TextField label="Détail (propre au point)" InputLabelProps={{shrink: true}} value={detail} onChange={(e) => setDetail(e.target.value)} size="small" fullWidth placeholder="ex. entrée par le 12 rue X"/>
                    <S3FileUpload label="Plan d'accès (optionnel)" folder="consignes" accept="application/pdf,image/*" value={planUrl} onChange={(u) => setPlanUrl(u)}/>
                    <Box>
                        <Button variant="contained" onClick={handleAdd} disabled={!consigne}>Affecter</Button>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fermer</Button>
            </DialogActions>
        </Dialog>
    );
}
